import {
  getSessionToken,
  isRefreshTokenExpired,
  isSessionTokenExpired,
  useDescope,
} from '@descope/react-sdk';
import toast from 'react-hot-toast';
import { create } from 'zustand';

import { crownApi, crownQueryClient } from '@/common/api/crownQueryClient';
import { getUserSession } from '@/common/user-session/userSessionApi';
import { clearAppState } from '@/common/util/clearAppState';
import { router } from '@/routing.tsx';

import {
  DelegatedPromise,
  LookupPromise,
  delegatedPromise,
  lookupPromise,
} from '@/common/user-session/utils';

// Sync all tabs on session swap
export const sessionChannel = new BroadcastChannel('crown_session');
const SESSION_EVENTS = {
  // One of the tabs flagged the session as no longer valid,
  // perform a soft logout (clear promises)
  EJECT: { type: 'EJECT' },
  // Master tab mounted a new session, init session promises again
  RESTART: { type: 'RESTART' },
} as const;
export type SessionEvent = (typeof SESSION_EVENTS)[keyof typeof SESSION_EVENTS];

const refreshOnLogout = !!import.meta.env.VITE_OSCILAR_DNB_SDK_URL;

// Test Checklist
// * All cases below tested twice - one with (1) refreshOnLogout=false and one with (2) refreshOnLogout=true
// [ ] Session swapping works
//     [ ] Magic -> Descope
//     [ ] Descope -> Magic
//     [ ] Descope -> Descope
//     [ ] Magic -> Magic
//     [ ] Magic -> Logout
//     [ ] Descope -> Logout
//     [ ] Logout -> Magic
//     [ ] Logout -> Descope
//     [ ] Logout -> Descope Biometrics -> Logout -> Descope Biometrics // This case doesn't use ?t links
// [ ] Public pages like /testing/users don't trigger any session logic
//     [ ] Session logic triggers as soon as public pages redirect to private pages (requireAuthenticated() or /login)
// [ ] On login complete should be triggered exactly once, show toast and contact the backend
// [ ] User can go to /logout to log out
// [ ] User can press a button in the header to log out
// [ ] Logout shows toast and redirects to /login with <Descope>
// [ ] Following bad ?t link shows expired link toast
// [ ] Errors during magic link should take user to /login
// [ ] Login should still work even if descope.logout errors during session swap
// [ ] User coming back after their session token expired, should still use their refresh token to continue the session
// [ ] User coming back after both session and refresh token expired should be presented with a login screen
// [ ] When logged in, user's name appears in the header and relevant sidebars and dropdowns
// [ ] /login shows spinner if user is swapping session
// [ ] /login shows <Descope> if user is logged out, even when refreshed
// [ ] /login redirects user if they are already logged in
// [ ] Logins and logouts affect all tabs, not just current tab
// [ ] Logged out user is remembered
// [ ] HMR rerenders smoothly (if not editing the state file)
export type UserSessionState = {
  descope: ReturnType<typeof useDescope> | null;
  initDescope: (descope: ReturnType<typeof useDescope>) => void;
  sessionChannelListener:
    | ((sessionEvent: MessageEvent<SessionEvent>) => void)
    | null;
  /** @returns loginMode */
  initSessionPromise: LookupPromise<
    'DESCOPE_FLOW' | 'MAGIC_LINK' | 'FROM_CACHE'
  > | null;
  initSession: () => UserSessionState['initSessionPromise'];
  /** Returns initJwt to compare */
  sessionSwapPromise: DelegatedPromise<string | undefined> | null;
  handleSessionSwap: () => UserSessionState['sessionSwapPromise'];

  sessionReady: boolean;

  logoutPromise: Promise<boolean> | null;
  /**
   * @param redirect Go to /login
   * @param soft Don't trigger descope.logout
   * @returns Boolean indicating whether or not it triggered a refresh
   * */
  logout: (params?: { redirect?: boolean; soft?: boolean }) => Promise<boolean>;

  completeLoginPromise: Promise<void> | null;
  completeLogin: () => Promise<void>;
};

// Order of execution:
// - Main component inits descope (from useDescope hook, as early as possible)
// - We hit either _root loader or requireAuthenticated(), or land on /login
//     Above decide whether or not we need to start caring about the session
//     As late as possible so that we don't check session expiration prematurely
// - First initSession() - determine login mode
// - handleSessionSwap() - logout previous user, follow magic links
// - (If applicable) Display <Descope> component
// - Either magic link promise or <Descope> finishes sessionSwapPromise.resolve
// - Subsequent initSession() - compare jwts until descope is stable
//     We no longer store initJwt in persist() - this was causing flaky behaviour
// - Trigger completeLogin() and DNB
// - sessionReady=true and useUserSession pulls user data from the backend
// - Later when user logs out, all promises are nullified and we start over
export const useUserSessionState = create<UserSessionState>((set, get) => ({
  descope: null,
  initDescope: (descope) => {
    if (!get().descope) set({ descope });
  },
  sessionChannelListener: null,
  initSessionPromise: null,
  initSession: () => {
    const state = get();
    const initialLoad = !state.initSessionPromise;

    if (initialLoad) {
      // Page just got mounted, let's figure out the session sources
      // - cached jwt and url token
      //
      // It is critical to collect this data before <Descope> component
      // has a chance to remove url params or update localStorage
      const initSessionPromise = lookupPromise(
        (async () => {
          if (!state.sessionChannelListener) {
            const sessionChannelListener = (
              sessionEvent: MessageEvent<SessionEvent>,
            ) => {
              if (sessionEvent.data.type === 'EJECT') {
                state.logout({ redirect: true, soft: true });
              }
              if (sessionEvent.data.type === 'RESTART') {
                set({
                  initSessionPromise: null,
                  sessionSwapPromise: null,
                  completeLoginPromise: null,
                  sessionReady: false,
                });
              }
            };
            sessionChannel.addEventListener('message', sessionChannelListener);
            set({ sessionChannelListener });
          }

          const qs = new URLSearchParams(window.location.search);
          const [linkToken, descopeFlow] = [
            qs.get('t'),
            qs.get('descope-login-flow'),
          ];
          // Login links of any kind override current session
          if (linkToken) {
            sessionChannel.postMessage(SESSION_EVENTS.EJECT);
            if (descopeFlow) return 'DESCOPE_FLOW';
            return 'MAGIC_LINK';
          }
          // Otherwise continue if possible
          const validSession =
            getSessionToken() &&
            (!isSessionTokenExpired() || !isRefreshTokenExpired());
          if (validSession) return 'FROM_CACHE';
          // Fallback to login page
          return 'DESCOPE_FLOW';
        })(),
      );
      set({ initSessionPromise });
      // Do not clear the initSessionPromise when it settles
      // It is logout's job

      return initSessionPromise;
    }

    // Not inital load - check for jwt updates
    //
    // Sometimes Descope's useSession and useUser may have broken state despite having a valid token
    // This may especially occur after magicLink.verify as it works outside of a typical descope flow
    // Grabbing the token straight from storage and comparing with the response bypasses this issue
    // There might also be a few renders of delay between successful login and the token being mounted
    // We might also be in middle of logging out previous session - hence we must compare tokens
    //
    // These Descope funcs have been found unreliable:
    // - useSession
    // - useUser
    // - descope.onSessionTokenChange
    // - descope.onUserChange
    // - descope.onIsAuthenticatedChange
    // Therefore we have to compare the jwts directly
    //
    // Note this fixes itself after a refresh later,
    // so we only have to do that during session swapping
    if (!state.sessionReady) {
      const mountedJwt = getSessionToken();
      // Once populated, initJwt will become outdated quickly due to refreshing
      // Note we can't really await in here, so we are using LookupPromise
      // That is okay as this function will keep being called often
      const initJwt = state.sessionSwapPromise?.result;
      if (mountedJwt && initJwt && mountedJwt === initJwt) {
        set({ sessionReady: true });
        // Nothing left to await, trigger post-login effects
        state.completeLogin();
      }
    }

    return state.initSessionPromise;
  },
  sessionSwapPromise: null,
  handleSessionSwap: () => {
    let state = get();
    if (state.sessionSwapPromise) return state.sessionSwapPromise;
    const sessionSwapPromise = delegatedPromise(async () => {
      if (!state.descope) {
        throw new Error('Login called too early');
      }
      if (!state.initSessionPromise) {
        state.initSession();
        state = get();
      }
      const loginMode = await state.initSessionPromise;

      // Continue the session, no session swap needed
      if (loginMode === 'FROM_CACHE') return getSessionToken();

      // Eject current session if needed
      if (getSessionToken()) {
        const isRefreshing = await state.logout({
          redirect: loginMode === 'DESCOPE_FLOW',
        });
        // Don't trigger side effects if hard refresh is in progress
        // Just return early and resume after the refresh
        //
        // Note: Hard refresh is required to unmount the DNB script
        // - it has no flushing mechanism for swapping users
        //
        // We need to return something to prevent <LoginForm> from thinking
        // we have deferred resolving to it
        if (isRefreshing) return 'PAGE_REFRESH';
      }

      try {
        if (loginMode === 'MAGIC_LINK') {
          // Wait for magic link
          const qs = new URLSearchParams(window.location.search);
          const linkToken = qs.get('t');
          const res = await state.descope!.magicLink.verify(linkToken!);
          qs.delete('t');
          await router.navigate({
            to: '/login',
            search: Object.fromEntries(qs.entries()),
            replace: true,
          });
          if (!res.ok || !res.data!.sessionJwt) throw res.error;
          return res.data!.sessionJwt;
        }
      } catch (error) {
        // If we failed to consume the magic link, show the login screen
        toast.error('The link you followed has expired');
        state.logout();
        throw error;
      }

      // For DESCOPE_FLOW, allow the <Descope> component to resolve this
      // DelegatedPromise lets us do that by returning undefined
      return;
    });

    set({ sessionSwapPromise: sessionSwapPromise });
    sessionSwapPromise.then(() => {
      state.initSession();
    });
    // Do not clear the sessionSwapPromise when it settles
    // It is logout's job

    return sessionSwapPromise;
  },

  sessionReady: false,

  logoutPromise: null,
  logout: async ({ redirect = true, soft = false } = {}) => {
    const state = get();
    if (state.logoutPromise) return state.logoutPromise;
    const previousUser = crownQueryClient.getQueryData<
      Awaited<ReturnType<typeof getUserSession>>
    >(['session']);

    const cleanup =
      !!previousUser ||
      !!state.sessionReady ||
      !!state.sessionSwapPromise?.result ||
      !!state.descope!.getSessionToken();

    const logoutPromise = (async () => {
      const qs = new URLSearchParams(window.location.search);
      if (cleanup && !soft) {
        try {
          sessionChannel.postMessage(SESSION_EVENTS.EJECT);
          await state.descope!.logout();
        } catch (error) {
          // We can still logout in the browser itself, not a blocker
          console.error(error);
        }
      }
      // Redirect=false indicates to us that we are in middle of a
      // session swap and we should retain the ongoing promises
      set({
        initSessionPromise: redirect
          ? lookupPromise(Promise.resolve('DESCOPE_FLOW'))
          : state.initSessionPromise,
        sessionSwapPromise: redirect ? null : state.sessionSwapPromise,
        completeLoginPromise: null,
        sessionReady: false,
      });
      clearAppState();
      if (redirect) {
        if (cleanup) toast.success('Logged out successfully');
        await router.navigate({
          to: '/login',
          search: Object.fromEntries(qs.entries()),
          replace: true,
        });
      }

      if (previousUser) {
        localStorage.setItem(
          'dls_last_user_display_name',
          previousUser.firstName,
        );
        localStorage.setItem('dls_last_user_login_id', previousUser.email);
      }

      void crownQueryClient.invalidateQueries();
      localStorage.removeItem('admin_history');

      if (refreshOnLogout) {
        // Oscilar DNB <script> doesn't have a flush mechanism
        // so we have to hard-reload the page on logout to avoid
        // cross-user pollution
        await new Promise((resolve) => {
          window.location.reload();
          setTimeout(() => resolve, 0);
        });
        return true;
      }

      return false;
    })();

    set({ logoutPromise });
    logoutPromise.finally(() => {
      set({ logoutPromise: null });
    });
    return logoutPromise;
  },

  completeLoginPromise: null,
  completeLogin: async () => {
    const state = get();
    const loginMode = state.initSessionPromise?.result;
    if (loginMode && loginMode !== 'FROM_CACHE') {
      sessionChannel.postMessage(SESSION_EVENTS.RESTART);
    }
    if (state.completeLoginPromise) return state.completeLoginPromise;
    const completeLoginPromise = (async () => {
      if (state.initSessionPromise?.result !== 'FROM_CACHE') {
        toast.success('Logged in successfully');
      }
      await crownApi.UserSession_onLogin();
    })();

    set({ completeLoginPromise });
    completeLoginPromise.finally(() => {
      set({ completeLoginPromise: null });
    });
    return completeLoginPromise;
  },
}));
